import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/feature/SectionHeading";
import NewsRow from "@/components/feature/NewsRow";
import WorkCard from "@/components/feature/WorkCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import QuickCategory from "@/pages/home/components/QuickCategory";
import HomeIntro from "@/pages/home/components/HomeIntro";
import useSEO from "@/hooks/useSEO";
import { siteStats } from "@/mocks/home";
import {
  getActressNames,
  getLatestNews,
  getPopularActresses,
  getPopularWorks,
} from "@/utils/data";

export default function Home() {
  useSEO({
    title: "日本女优大全 - 最新女优资料与影视作品介绍",
    description:
      "日本女优网站提供丰富的日本女优资料库，涵盖女优简介、影视作品及相关新闻，帮助用户全面了解日本女优信息。",
    keywords: "日本女优,日本女优资料,日本女优作品,日本女优介绍,日本女优影视",
  });

  const popularWorks = getPopularWorks(12);
  const latestNews = getLatestNews(5);
  const popularActresses = getPopularActresses(5);

  return (
    <SiteLayout>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4 border-b border-background-200 pb-5">
        <div>
          <p className="font-label text-[11px] tracking-[0.2em] text-primary-600">JAPAN ACTRESS ARCHIVE</p>
          <h1 className="mt-1.5 font-heading text-[26px] font-black leading-tight text-foreground-950 md:text-[32px]">
            日本女优大全 · 女优资料与影视作品索引
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            系统整理女优档案、出演作品与行业动态。支持多维筛选、站内联想搜索与相关推荐，为研究与浏览提供清晰入口。
          </p>
        </div>
        <dl className="flex items-center gap-6">
          {siteStats.map((stat) => (
            <div key={stat.label}>
              <dt className="font-label text-[11px] text-foreground-500">{stat.label}</dt>
              <dd className="font-heading text-2xl font-black text-primary-700">
                {stat.value}
                <span className="ml-0.5 font-label text-[11px] font-normal text-foreground-500">
                  {stat.unit}
                </span>
              </dd>
            </div>
          ))}
        </dl>
      </div>

      <HeroCarousel />

      <section className="mt-12">
        <SectionHeading
          eyebrow="QUICK CATEGORY"
          title="从分类开始浏览女优档案"
          description="按年龄段、出道年份或活跃程度进入分类页，筛选条件会自动带入。"
          actionLabel="完整筛选条件"
          actionHref="/category"
        />
        <QuickCategory />
      </section>

      <section className="mt-14">
        <SectionHeading
          eyebrow="POPULAR WORKS"
          title="热门影视作品"
          description="按站内浏览量综合排序的 12 部作品，涵盖电影、电视剧、OVA 与网络剧。"
          actionLabel="查看全部作品"
          actionHref="/work-list"
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
          {popularWorks.map((work) => (
            <WorkCard key={work.id} work={work} actressNames={getActressNames(work)} />
          ))}
        </div>
      </section>

      <section className="mt-14 grid gap-6 lg:grid-cols-[1fr_320px]">
        <div>
          <SectionHeading
            eyebrow="LATEST NEWS"
            title="最新新闻动态"
            description="作品资讯、人物动态与行业观察，按发布时间倒序更新。"
            actionLabel="进入新闻列表"
            actionHref="/news"
          />
          <div className="rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
            {latestNews.map((item) => (
              <NewsRow key={item.id} item={item} />
            ))}
          </div>
        </div>

        <aside className="space-y-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="flex items-center gap-1.5 font-heading text-base font-bold text-foreground-950">
              <i className="ri-fire-line text-primary-600" aria-hidden="true"></i>
              人气女优 Top 5
            </h3>
            <p className="mt-1 font-label text-[11px] text-foreground-500">综合浏览量、作品数与近期话题度</p>
            <ol className="mt-4 space-y-3">
              {popularActresses.map((actress, index) => (
                <li key={actress.id}>
                  <Link
                    to={`/detail/actress/${actress.id}`}
                    className="group flex items-center gap-3"
                  >
                    <span className="w-4 shrink-0 font-heading text-sm font-black text-primary-300">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="h-11 w-11 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={actress.portrait}
                        alt={`${actress.name} 女优资料写真`}
                        title={`${actress.name} 日本女优资料`}
                        className="h-full w-full object-cover object-top"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-label text-sm text-foreground-900 transition-colors group-hover:text-primary-700">
                        {actress.name}
                      </span>
                      <span className="block truncate font-label text-[11px] text-foreground-500">
                        {actress.agency} · {actress.worksCount} 部
                      </span>
                    </span>
                    <i className="ri-arrow-right-s-line text-foreground-300" aria-hidden="true"></i>
                  </Link>
                </li>
              ))}
            </ol>
            <Link
              to="/category?sort=works"
              className="mt-4 inline-flex w-full items-center justify-center gap-1 rounded-md border border-background-300 py-2 font-label text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              查看高产演员排行
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>

          <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
            <h3 className="flex items-center gap-1.5 font-heading text-base font-bold text-accent-950">
              <i className="ri-heart-3-line" aria-hidden="true"></i>
              收藏你关注的女优
            </h3>
            <p className="mt-2 font-label text-xs leading-relaxed text-accent-900/80">
              在女优详情页点击收藏，条目会保存在当前浏览器，并可从顶栏随时查看。该功能为前端演示，未接入后端账户。
            </p>
          </div>
        </aside>
      </section>

      <HomeIntro />
    </SiteLayout>
  );
}